import { Component, OnInit, ViewChild, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Actions, ofActionCompleted, Select, Store } from '@ngxs/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Observable } from 'rxjs';
import { Note, NotesPaginationRequest } from '../notes.types';
import { NotesActions } from '../state/actions';
import { NotesState } from '../state/state';
import { Module, NewPermission } from '@core/enums';
import { UserState } from '@state/user/state';

@Component({
  selector: 'notes-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NotesListComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  isLoading: boolean;

  @ViewChild(MatPaginator)
  private paginator: MatPaginator;

  private currentNote: number;

  @Select(NotesState.getNotesList) notes$: Observable<Note[]>;
  @Select(NotesState.getSelectedNote) selectedNote$: Observable<Note>;
  @Select(NotesState.getNotesPagination) pagination$: Observable<Note[]>;

  NewPermission = NewPermission;

  private get module(): string | undefined {
    return this.routeData?.module;
  }

  private get routeData(): any {
    return this.activatedRoute.snapshot.data;
  }

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store,
    private readonly actions$: Actions,
  ) {
    super();
    this.isLoading = false;
    this.actions$.pipe(ofActionCompleted(NotesActions.CreateNote), untilComponentDestroyed(this)).subscribe(() => {
      this.loadPage(0);
    });
    this.actions$.pipe(ofActionCompleted(NotesActions.UpdateNote), untilComponentDestroyed(this)).subscribe(() => {
      this.loadPage(this.store.selectSnapshot(NotesState.getNotesPagination).page, true);
    });
    this.actions$.pipe(ofActionCompleted(NotesActions.DeleteNote), untilComponentDestroyed(this)).subscribe(() => {
      let page = this.store.selectSnapshot(NotesState.getNotesPagination).page;
      if (this.store.selectSnapshot(NotesState.getNotesList).length <= 1 && page > 0) {
        page = this.store.selectSnapshot(NotesState.getNotesPagination).page - 1;
      } else {
        page = this.store.selectSnapshot(NotesState.getNotesPagination).page;
      }
      this.loadPage(page, false);
    });
    this.selectedNote$.pipe(untilComponentDestroyed(this)).subscribe((note) => {
      if (note?.id && this.currentNote !== note.id) {
        this.currentNote = note.id;
        this.navigateToChild(note.id, this.activatedRoute);
      }
    });
  }

  ngOnInit(): void {
    this.checkRedirectToFirstPipeline(this.activatedRoute);

    this.router.events.pipe(untilComponentDestroyed(this)).subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.checkRedirectToFirstPipeline(this.activatedRoute);
      }
    });
  }

  ngAfterViewInit(): void {
    this.paginator.page.pipe(untilComponentDestroyed(this)).subscribe((pageChange) => {
      this.loadPage(pageChange.pageIndex);
    });
  }

  private loadPage(page: number, keepSelected = false): void {
    const filter: NotesPaginationRequest = {
      perPage: 10,
      page: page,
      noteableType: this.store.selectSnapshot(NotesState.getNoteableType),
      noteableId: this.store.selectSnapshot(NotesState.getNoteableId),
    };
    this.store.dispatch(new NotesActions.GetNotes(filter, keepSelected));
  }

  private checkRedirectToFirstPipeline(route: ActivatedRoute): void {
    const listSnapshot = this.store.selectSnapshot(NotesState.getNotesList);
    if (!route.snapshot.firstChild && listSnapshot.length > 0) {
      this.navigateToChild(listSnapshot[0].id, route);
    }
  }

  private navigateToChild(id: number, route: ActivatedRoute): void {
    this.router.navigate(['./', id], { relativeTo: route });
  }

  addNote(): void {
    this.store.dispatch(new NotesActions.CreateNote());
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  getEditPermission(): boolean {
    if (this.module === Module.PROPERTIES) {
      return (
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.PROPERTIES_EDIT)) &&
        this.routeData.property.permissions.is_editable
      );
    } else if (this.module === Module.DEALS) {
      return (
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_EDIT)) &&
        this.routeData.project.permissions.is_editable
      );
    }

    return true;
  }
}
